/**
 * Utilitaire de conversion des filtres entre ApiSearchForm et Vuex Store
 * 
 * Gère les conversions spécifiques entre les formats :
 * - ApiSearchForm : Format unifié et typé
 * - Vuex Store : Format legacy avec champs séparés
 */

/**
 * Convertit les filtres du format ApiSearchForm vers le format Vuex Store
 * @param {Object} apiFilters - Filtres du format ApiSearchForm
 * @param {Object} userContext - Contexte utilisateur (métier, etc.)
 * @returns {Object} Filtres au format Vuex Store
 */
export function convertApiFiltersToStore(apiFilters, userContext = {}) {
  const storeFilters = { ...apiFilters }
  const { metier } = userContext
  
  
  // Conversion des champs range (surface, price)
  convertRangeFields(storeFilters, apiFilters)
  
  // Conversion des champs date
  convertDateFields(storeFilters, apiFilters)
  
  // Conversion des types d'objets selon le métier
  convertObjectTypeFields(storeFilters, apiFilters, metier)
  
  //  Conversion des champs user/contact
  convertUserContactFields(storeFilters, apiFilters)
  
  //  Conversion du négociateur (Contact Manager)
  convertNegociateurField(storeFilters, apiFilters)
  
  //  Conversion du preset
  convertPresetField(storeFilters, apiFilters)
  
  return storeFilters
}

/**
 * Convertit les champs de type range
 * Format API: { surface: { surfaceGt: 50, surfaceLt: 100 } }
 * Format Store: { surfaceGt: 50, surfaceLt: 100 }
 */
function convertRangeFields(storeFilters, apiFilters) {
  const rangeFields = ['surface', 'price']
  
  rangeFields.forEach(fieldName => {
    if (apiFilters[fieldName] !== undefined) {
      if (apiFilters[fieldName] && typeof apiFilters[fieldName] === 'object') {
        const range = apiFilters[fieldName]
        const gtField = `${fieldName}Gt`
        const ltField = `${fieldName}Lt`

        if (Array.isArray(range)) {
          storeFilters[gtField] = range[0] !== undefined && range[0] !== null ? range[0] : null
          storeFilters[ltField] = range[1] !== undefined && range[1] !== null ? range[1] : null
        } else {
          storeFilters[gtField] = range[gtField] !== undefined ? range[gtField] : null
          storeFilters[ltField] = range[ltField] !== undefined ? range[ltField] : null

          // Propager les clés custom issues de l'eventMapping (ex: surfaceProgrammeMin/Max)
          Object.keys(range).forEach(key => {
            if (key !== gtField && key !== ltField) {
              storeFilters[key] = range[key]
            }
          })
        }
      } else {
        storeFilters[`${fieldName}Gt`] = null
        storeFilters[`${fieldName}Lt`] = null
      }
      delete storeFilters[fieldName]
    }
  })
}

/**
 * Convertit les champs de type date
 * Format API: { dateCreation: [startDate, endDate] }
 * Format Store: { dateCreationGt: startDate, dateCreationLt: endDate }
 * 
 * OU pour Contact Manager:
 * Format API: { dateRange: [startDate, endDate] }
 * Format Store: { beforeContacts: startDate, afterContacts: endDate }
 */
function convertDateFields(storeFilters, apiFilters) {
  // Gestion de dateCreation (pages annonces)
  if (apiFilters.dateCreation !== undefined) {
    if (apiFilters.dateCreation && Array.isArray(apiFilters.dateCreation)) {
      const [startDate, endDate] = apiFilters.dateCreation
      storeFilters.dateCreationGt = startDate || null
      storeFilters.dateCreationLt = endDate || null
    } else {
      storeFilters.dateCreationGt = null
      storeFilters.dateCreationLt = null
    }
    delete storeFilters.dateCreation
  }
  
  // Gestion de dateRange (Contact Manager)
  // Le mapping vers beforeContacts/afterContacts est géré par ApiSearchForm (eventMapping).
  // On supprime dateRange pour ne pas l'envoyer au backend tel quel.
  if (apiFilters.dateRange !== undefined) {
    delete storeFilters.dateRange
  }
}

/**
 * Convertit les champs user/contact pour compatibilité avec l'ancien système
 * - user : garde l'ID numérique (compatible)
 * - contact : convertit l'ID vers le fullname (requis par l'API)
 */
function convertUserContactFields(storeFilters, apiFilters) {
  // User : garder tel quel (ID numérique) - compatible avec l'ancien système
  // Pas de conversion nécessaire pour user
  
  // Contact : maintenant corrigé via valueField='label' dans les fixtures
  // Le composant GenericFieldSelect utilise désormais le fullname au lieu de l'ID
}

/**
 * Convertit le champ négociateur pour Contact Manager
 * Format API: { negociateur: 'CODE_USER' }
 * Format Store/Backend: { negociateur: 'CODE_USER', negociateur_code: 'CODE_USER' }
 * 
 * Note: On garde les DEUX champs car:
 * - contacts.js utilise directement filter.negociateur
 * - portals.js et l'API backend utilisent negociateur_code
 */
function convertNegociateurField(storeFilters, apiFilters) {
  if (apiFilters.negociateur !== undefined && apiFilters.negociateur !== null && apiFilters.negociateur !== '') {
    // Ajouter negociateur_code SANS supprimer negociateur
    storeFilters.negociateur_code = apiFilters.negociateur
    // NE PAS supprimer negociateur car contacts.js l'utilise directement
  } else {
    // Si negociateur est vide/null, marquer les DEUX champs comme undefined pour les supprimer du store
    storeFilters.negociateur = undefined
    storeFilters.negociateur_code = undefined
  }
}

/**
 * Convertit le preset en valeur withoutPictures pour compatibilité avec l'ancien système
 * Le preset 'withoutPictures' doit activer le flag withoutPictures dans le store
 * qui sera ensuite converti en sansPhoto='O' par formatFilters
 */
function convertPresetField(storeFilters, apiFilters) {
  if (apiFilters.preset === 'withoutPictures') {
    storeFilters.withoutPictures = true
  } else {
    storeFilters.withoutPictures = null
  }
}

/**
 * Convertit les types d'objets selon le métier
 * - Promoteurs : objectTypes (radio string) -> typeObjets (array numbers)
 * - Bailleurs sociaux : typeObjets (radio string) -> typeObjets (array numbers)
 * - Autres : objectTypes reste objectTypes (cascader array)
 */
function convertObjectTypeFields(storeFilters, apiFilters, metier) {
  const isPromoteur = metier === 'PROMOTEUR'
  const isBailleurSocial = metier === 'BAILLEUR_SOCIAL'
  const isCmi = metier === 'CMI'
  
  // Promoteurs : objectTypes (radio) -> typeObjets (array)
  if (apiFilters.objectTypes !== undefined && isPromoteur) {
    if (apiFilters.objectTypes && apiFilters.objectTypes !== '0') {
      const numValue = parseInt(apiFilters.objectTypes, 10)
      storeFilters.typeObjets = !isNaN(numValue) ? [numValue] : []
    } else {
      storeFilters.typeObjets = []
    }
    storeFilters.objectTypes = null // Éviter conflit dans formatFilters
  }
  
  // Bailleurs sociaux : typeObjets (radio) -> typeObjets (array)
  if (apiFilters.typeObjets !== undefined && isBailleurSocial) {
    if (apiFilters.typeObjets && apiFilters.typeObjets !== '0') {
      const numValue = parseInt(apiFilters.typeObjets, 10)
      storeFilters.typeObjets = !isNaN(numValue) ? [numValue] : []
    } else {
      storeFilters.typeObjets = []
    }
  }
  
  // CMI : typeObjets (radio) -> typeObjets (array)
  if (apiFilters.typeObjets !== undefined && isCmi) {
    if (apiFilters.typeObjets && apiFilters.typeObjets !== 'all') {
      const numValue = parseInt(apiFilters.typeObjets, 10)
      storeFilters.typeObjets = !isNaN(numValue) ? [numValue] : []
    } else {
      storeFilters.typeObjets = []
    }
  }
}

/**
 * Convertit les filtres du format Vuex Store vers le format ApiSearchForm
 * @param {Object} storeFilters - Filtres du format Vuex Store
 * @param {Object} userContext - Contexte utilisateur
 * @returns {Object} Filtres au format ApiSearchForm
 */
export function convertStoreFiltersToApi(storeFilters, userContext = {}) {
  const apiFilters = { ...storeFilters }
  
  // Conversion inverse des champs range
  convertStoreRangeFields(apiFilters, storeFilters)
  
  // Conversion inverse des champs date
  convertStoreDateFields(apiFilters, storeFilters)
  
  // Conversion inverse des types d'objets
  convertStoreObjectTypeFields(apiFilters, storeFilters, userContext.metier)
  
  return apiFilters
}

/**
 * Conversion inverse des champs range pour l'affichage initial
 */
function convertStoreRangeFields(apiFilters, storeFilters) {
  const rangeFields = ['surface', 'price']
  
  rangeFields.forEach(fieldName => {
    const gtField = `${fieldName}Gt`
    const ltField = `${fieldName}Lt`
    
    if (storeFilters[gtField] !== undefined || storeFilters[ltField] !== undefined) {
      apiFilters[fieldName] = {
        [gtField]: storeFilters[gtField],
        [ltField]: storeFilters[ltField]
      }
      delete apiFilters[gtField]
      delete apiFilters[ltField]
    }
  })
}

/**
 * Conversion inverse des champs date
 */
function convertStoreDateFields(apiFilters, storeFilters) {
  if (storeFilters.dateCreationGt !== undefined || storeFilters.dateCreationLt !== undefined) {
    const startDate = storeFilters.dateCreationGt
    const endDate = storeFilters.dateCreationLt
    
    if (startDate || endDate) {
      apiFilters.dateCreation = [startDate, endDate]
    } else {
      apiFilters.dateCreation = null
    }
    
    delete apiFilters.dateCreationGt
    delete apiFilters.dateCreationLt
  }
}

/**
 * Conversion inverse des types d'objets
 */
function convertStoreObjectTypeFields(apiFilters, storeFilters, metier) {
  const isPromoteur = metier === 'PROMOTEUR'
  const isBailleurSocial = metier === 'BAILLEUR_SOCIAL'
  const isCmi = metier === 'CMI'
  
  // Promoteurs : typeObjets (array) -> objectTypes (string)
  if (storeFilters.typeObjets !== undefined && isPromoteur) {
    if (Array.isArray(storeFilters.typeObjets) && storeFilters.typeObjets.length > 0) {
      apiFilters.objectTypes = storeFilters.typeObjets[0].toString()
    } else {
      apiFilters.objectTypes = '0' // "Tous"
    }
    delete apiFilters.typeObjets
  }
  
  // Bailleurs sociaux : typeObjets (array) -> typeObjets (string)
  if (storeFilters.typeObjets !== undefined && isBailleurSocial) {
    if (Array.isArray(storeFilters.typeObjets) && storeFilters.typeObjets.length > 0) {
      apiFilters.typeObjets = storeFilters.typeObjets[0].toString()
    } else {
      apiFilters.typeObjets = '0' // "Tous"
    }
  }
  
  // CMI : typeObjets (array) -> typeObjets (string)
  if (storeFilters.typeObjets !== undefined && isCmi) {
    if (Array.isArray(storeFilters.typeObjets) && storeFilters.typeObjets.length > 0) {
      apiFilters.typeObjets = storeFilters.typeObjets[0].toString()
    } else {
      apiFilters.typeObjets = 'all' // "Tous"
    }
  }
}
